import { useFetchImgObjUrl } from "@/hooks/useFetchImgObjUrl";
import {
  selectActiveAreaIdx,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";
import { getDetectionBoxGeometry } from "@/utils/detection-box-geometry";
import { cn } from "@/utils/utils";
import { OrientedBoxOverlay } from "./OrientedBoxOverlay";
import { ImageContextSchema } from "@augmodo/lebron-browser-client";
import { mvpApiGetProductOptions } from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import { HoverCard } from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useMemo, useRef, useState } from "react";

interface Props {
  productDetection: any;
  referenceProductDetectionIdx: number;
  reference: ImageContextSchema;
  isMatchingProduct?: boolean;
  referenceImgIdx: number;
}

export const ReferenceProductDetectionBox = (props: Props) => {
  const {
    productDetection,
    referenceProductDetectionIdx,
    reference,
    referenceImgIdx,
    isMatchingProduct = false,
  } = props;
  const points = productDetection?.points ?? [];
  // The div is the hit area; the overlay strokes the detection's shape.
  const { style: boundingBoxStyle, corners } = useMemo(
    () => getDetectionBoxGeometry(points),
    [points],
  );
  const setActiveProductId = useCoverageBetaStore((s) => s.setActiveProductId);
  const product_id =
    reference?.product_ids?.[referenceProductDetectionIdx] ?? "";

  const isActive = useCoverageBetaStore(
    (s) => s.activeProductId === product_id,
  );

  // Highlighted as a neighbour of the hovered empty area (teal), to help the
  // labeler locate the region across the image and planogram.
  const isNeighbourHighlighted = useCoverageBetaStore(
    (s) => !!product_id && s.neighbourProductIds.includes(product_id),
  );

  // Highlight this detection box whenever it is selected for the active area.
  // Matched by detection index (so the highlight survives a confirm-step
  // product overwrite), or by product_id for the cross-source case (a product
  // picked on the planogram highlights the matching reference box).
  const isSelectedProduct = useCoverageBetaStore((s) => {
    const area = s.idxToFeEmptyArea[selectActiveAreaIdx(s)];
    return (
      area?.selectedProducts.some((p) =>
        p.source === "reference"
          ? p.referenceImgIdx === referenceImgIdx &&
            p.referenceProductDetectionIdx === referenceProductDetectionIdx
          : !!product_id && p.id === product_id,
      ) ?? false
    );
  });

  // While a product is awaiting confirmation, only the box being confirmed is
  // interactive (to unselect it); every other product box is blocked until the
  // pending one is resolved (confirmed / no match / unselected).
  const isBlockedByConfirm = useCoverageBetaStore((s) => {
    const c = s.confirming;
    return (
      c != null &&
      !(
        c.referenceImgIdx === referenceImgIdx &&
        c.referenceProductDetectionIdx === referenceProductDetectionIdx
      )
    );
  });

  const queryClient = useQueryClient();
  const [isHovered, setIsHovered] = useState(false);
  // Pointer position at mousedown, so a drag (panning the image) isn't treated
  // as a click that selects the product. Mirrors OneLabelBox on the planogram.
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
  // Product metadata is small JSON and is what makes hover + click feel instant,
  // so fetch it up front (react-query keeps this off the render's critical path).
  // The product IMAGE, though, is heavy and only appears in the hover card — the
  // eager per-box image download across every mounted reference image was what
  // flooded the network on task open, so it stays lazy (loads on hover).
  const { data: productResp } = useQuery({
    ...mvpApiGetProductOptions({ path: { product_id } }),
    enabled: !!product_id,
  });
  const productDescription = productResp?.description ?? "";
  const img_url = productResp?.image_url ?? "";
  const { data: imgObjUrlResp } = useFetchImgObjUrl({
    img_id: product_id,
    image_url: isHovered ? img_url : "",
  });
  const productImgObjUrl = imgObjUrlResp || "";

  const handleClick = async () => {
    const {
      idxToFeEmptyArea,
      setIdxToFeEmptyArea,
      activeEmptyAreaIdx,
      setConfirming,
      confirming,
      isReadOnly,
      nudgeConfirm,
    } = useCoverageBetaStore.getState();

    // Read-only (admin) view: selections are fixed as submitted.
    if (isReadOnly) return;

    // Can't attach products to a deleted (or missing) empty area.
    const activeArea = idxToFeEmptyArea[activeEmptyAreaIdx];
    if (!activeArea || activeArea.isDeleted) return;

    // Block interacting with any product other than the one being confirmed —
    // and nudge the confirm panel so the user sees why the click did nothing.
    const isConfirmingThisBox =
      confirming != null &&
      confirming.referenceImgIdx === referenceImgIdx &&
      confirming.referenceProductDetectionIdx === referenceProductDetectionIdx;
    if (confirming != null && !isConfirmingThisBox) {
      nudgeConfirm();
      return;
    }

    // Match this detection by its indices, not product_id — the confirm step
    // can overwrite the stored product, so the detection index is the stable id.
    const isThisDetection = (p: (typeof activeArea.selectedProducts)[number]) =>
      p.source === "reference" &&
      p.referenceImgIdx === referenceImgIdx &&
      p.referenceProductDetectionIdx === referenceProductDetectionIdx;
    const wasSelected = activeArea.selectedProducts.some(isThisDetection);

    // Unselect: doesn't need the product payload, so handle it synchronously.
    if (wasSelected) {
      setIdxToFeEmptyArea(
        produce(idxToFeEmptyArea, (draft) => {
          const area = draft[activeEmptyAreaIdx];
          if (!area) return;
          area.selectedProducts = area.selectedProducts.filter(
            (p) => !isThisDetection(p),
          );
          if (area.selectedProducts.length === 0) area.isValid = false;
          // Removing a product voids any prior confirmation for this area, so
          // the stale confirmed id can't be submitted.
          area.confirmedProductId = null;
        }),
      );
      // Unselected the product being confirmed → close the confirm step.
      if (isConfirmingThisBox) setConfirming(null);
      return;
    }

    // Select: read the product from the eager-loaded cache (instant); on a slow
    // connection where the up-front fetch hasn't landed yet, this awaits it
    // rather than no-opping the click.
    let product;
    try {
      product = await queryClient.fetchQuery(
        mvpApiGetProductOptions({ path: { product_id } }),
      );
    } catch {
      return;
    }
    if (!product) return;

    // Re-read state after the await; the click targets the area that was active
    // when it fired.
    const {
      idxToFeEmptyArea: freshIdx,
      setIdxToFeEmptyArea: setFresh,
      setConfirming: setConfirmingFresh,
    } = useCoverageBetaStore.getState();
    const freshArea = freshIdx[activeEmptyAreaIdx];
    if (!freshArea || freshArea.isDeleted) return;

    setFresh(
      produce(freshIdx, (draft) => {
        const area = draft[activeEmptyAreaIdx];
        if (!area) return;
        area.selectedProducts.push({
          ...product,
          source: "reference",
          referenceImgIdx,
          referenceProductDetectionIdx,
          // Keep the model's original product so the confirm step can overwrite
          // the shown/selected product while the payload still reports what was
          // detected as `identified_product_id`.
          identifiedProductId: product.id,
        });
        area.isValid = true;
        // A product-bearing (confirmed) area must be visible.
        area.isHidden = false;
      }),
    );

    // Selecting a product via the image opens the confirm-product step.
    setConfirmingFresh({
      emptyAreaIdx: activeEmptyAreaIdx,
      referenceImgIdx,
      referenceProductDetectionIdx,
    });
  };

  // Selection wins, then the neighbour highlight, then the match highlight.
  let strokeClassName = "stroke-gray-100/50";
  if (isSelectedProduct) strokeClassName = "stroke-green-400/75";
  else if (isNeighbourHighlighted) strokeClassName = "stroke-teal-400";
  else if (isMatchingProduct) strokeClassName = "stroke-blue-400";

  let ringClassName;
  if (isNeighbourHighlighted) ringClassName = "stroke-teal-400";
  else if (isActive) ringClassName = "stroke-yellow-200";

  return (
    <HoverCard
      position="right-start"
      offset={8}
      withinPortal
      shadow="md"
      openDelay={0}
      closeDelay={0}
      middlewares={{ flip: true, shift: true }}
      styles={{ dropdown: { pointerEvents: "none" } }}
    >
      <HoverCard.Target>
        <div
          data-product-index={referenceProductDetectionIdx}
          className={cn(
            "absolute cursor-pointer",
            isBlockedByConfirm && "cursor-not-allowed",
          )}
          style={{ ...boundingBoxStyle }}
          onMouseEnter={() => {
            setActiveProductId(product_id);
            setIsHovered(true);
          }}
          onMouseLeave={() => {
            setActiveProductId("");
          }}
          onMouseDown={(e) => {
            mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
          }}
          onClick={(e) => {
            // Ignore the click if the pointer moved between down and up — the
            // user was panning the image, not selecting this box.
            const down = mouseDownPosRef.current;
            mouseDownPosRef.current = null;
            if (down) {
              const dx = e.clientX - down.x;
              const dy = e.clientY - down.y;
              if (dx * dx + dy * dy > 25) return;
            }
            handleClick();
          }}
        >
          <OrientedBoxOverlay
            corners={corners}
            className={strokeClassName}
            strokeWidth={isSelectedProduct ? 2 : 1}
            ringClassName={ringClassName}
          />
        </div>
      </HoverCard.Target>

      <HoverCard.Dropdown p={8}>
        <div className="w-48 h-auto bg-white">
          <div className="h-40 w-full bg-gray-200 rounded">
            {productImgObjUrl && (
              <img
                src={productImgObjUrl}
                className="size-full object-contain"
              />
            )}
          </div>
          <div className="h-2" />
          <div className="text-sm font-semibold">{productDescription}</div>
        </div>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

// {
//     "id": "46c70d5d-79ed-4b59-85f3-ddd010a3c3e0",
//     "points": [ .., .., .., ..  ],
//     "class_index": 1,
//     "confidence": 0.71728515625
// }
